<script setup lang="ts">
import { TrendingDown, TrendingUp } from 'lucide-vue-next';
import type { Component, HTMLAttributes } from 'vue';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface Props {
    title: string;
    value: string | number;
    icon?: Component;
    trend?: {
        value: string | number;
        positive: boolean;
        label?: string;
    };
    class?: HTMLAttributes['class'];
}

defineProps<Props>();
</script>

<template>
    <Card
        :class="
            cn('overflow-hidden rounded-2xl border-border/40', $props.class)
        "
    >
        <CardContent class="p-6">
            <div class="flex items-center justify-between space-x-4">
                <div class="space-y-1">
                    <p
                        class="text-sm leading-none font-medium text-muted-foreground"
                    >
                        {{ title }}
                    </p>
                    <div class="text-2xl font-semibold tracking-tight">
                        {{ value }}
                    </div>
                </div>
                <div
                    v-if="icon"
                    class="flex h-12 w-12 items-center justify-center rounded-full bg-brand/10 text-brand"
                >
                    <component :is="icon" class="h-6 w-6" />
                </div>
            </div>
            <div v-if="trend" class="mt-4 flex items-center text-sm">
                <span
                    :class="
                        cn(
                            'mr-2 flex items-center font-medium',
                            trend.positive
                                ? 'text-emerald-600 dark:text-emerald-400'
                                : 'text-rose-600 dark:text-rose-400',
                        )
                    "
                >
                    <TrendingUp v-if="trend.positive" class="mr-1 h-4 w-4" />
                    <TrendingDown v-else class="mr-1 h-4 w-4" />
                    {{ trend.value }}
                </span>
                <span v-if="trend.label" class="text-muted-foreground">
                    {{ trend.label }}
                </span>
            </div>
        </CardContent>
    </Card>
</template>
